import { useState, useEffect } from 'react';
import { Plus } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import type { Boxer, Sponsor } from '@/types/boxer';
import type { Employee } from '@/types/auth';
import { LICENSE_FEE } from '@/lib/licenseUtils';

interface BoxerRegistrationProps {
  onRegister: (boxer: Boxer) => void;
  existingBoxers: Boxer[];
  existingSponsors: Sponsor[];
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

export function BoxerRegistration({ onRegister, existingBoxers = [], existingSponsors = [] }: BoxerRegistrationProps) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [formData, setFormData] = useState({
    stateId: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    sponsor: '',
    profileImage: '',
    timezone: 'NA' as 'NA' | 'EU' | 'AU',
    feePaid: false,
    signedUpBy: '',
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/employees`);
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.stateId || !formData.firstName || !formData.lastName || !formData.phoneNumber) {
      toast.error('Please fill in all required fields');
      return;
    }

    const boxerList = existingBoxers ?? [];
    const duplicateStateId = boxerList.find(
      (boxer) => boxer.stateId.toLowerCase() === formData.stateId.toLowerCase()
    );

    if (duplicateStateId) {
      toast.error(`State ID ${formData.stateId} is already registered to ${duplicateStateId.firstName} ${duplicateStateId.lastName}`);
      return;
    }

    const now = new Date().toISOString();
    
    const newBoxer: Boxer = {
      id: `boxer-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      stateId: formData.stateId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phoneNumber: formData.phoneNumber,
      sponsor: formData.sponsor,
      wins: 0,
      losses: 0,
      knockouts: 0,
      rankingPoints: 0,
      registeredDate: now,
      profileImage: formData.profileImage || undefined,
      fightHistory: [],
      timezone: formData.timezone,
      licenseStatus: 'active',
      lastPaymentDate: now,
      licenseFee: LICENSE_FEE,
      feePaid: formData.feePaid,
      signedUpBy: formData.signedUpBy || undefined,
    };

    onRegister(newBoxer);
    const paymentStatus = formData.feePaid ? 'Fee paid' : 'Payment pending';
    toast.success(`${formData.firstName} ${formData.lastName} registered successfully! ${paymentStatus}`);
    
    setFormData({
      stateId: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      sponsor: '',
      profileImage: '',
      timezone: 'NA',
      feePaid: false,
      signedUpBy: '',
    });
  };

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-display uppercase text-secondary">Register New Boxer</h2>
        <div className="mt-3 p-4 bg-accent/10 border border-accent/30 rounded-lg">
          <p className="text-sm font-medium text-accent-foreground">
            Registration Fee: ${LICENSE_FEE.toLocaleString()} (due monthly)
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Boxers must maintain an active license to be eligible for fight cards
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="state-id">State ID *</Label>
            <Input
              id="state-id"
              placeholder="State ID Number"
              value={formData.stateId}
              onChange={(e) => setFormData({ ...formData, stateId: e.target.value })}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone Number *</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              required
              className="mt-1"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first-name">First Name *</Label>
            <Input
              id="first-name"
              placeholder="First Name"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="last-name">Last Name *</Label>
            <Input
              id="last-name"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="sponsor">Sponsor (Optional)</Label>
          <Select
            value={formData.sponsor}
            onValueChange={(value) => setFormData({ ...formData, sponsor: value === 'none' ? '' : value })}
          >
            <SelectTrigger id="sponsor" className="mt-1">
              <SelectValue placeholder="Select a sponsor (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Sponsor</SelectItem>
              {existingSponsors.map((sponsor) => (
                <SelectItem key={sponsor.id} value={sponsor.name}>
                  {sponsor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="signedUpBy">Signed Up By (Optional)</Label>
          <Select
            value={formData.signedUpBy}
            onValueChange={(value) => setFormData({ ...formData, signedUpBy: value === 'none' ? '' : value })}
          >
            <SelectTrigger id="signedUpBy" className="mt-1">
              <SelectValue placeholder="Select employee (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not tracked</SelectItem>
              {employees.map((employee) => (
                <SelectItem key={employee.id} value={employee.id}>
                  {employee.name} - {employee.position}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="timezone">Timezone *</Label>
          <Select
            value={formData.timezone}
            onValueChange={(value) => setFormData({ ...formData, timezone: value as 'NA' | 'EU' | 'AU' })}
          >
            <SelectTrigger id="timezone" className="mt-1">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NA">North America (NA)</SelectItem>
              <SelectItem value="EU">Europe (EU)</SelectItem>
              <SelectItem value="AU">Australia (AU)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="profile-image">Profile Image URL</Label>
          <Input
            id="profile-image"
            placeholder="https://example.com/photo.jpg (Optional)"
            value={formData.profileImage}
            onChange={(e) => setFormData({ ...formData, profileImage: e.target.value })}
            className="mt-1"
          />
        </div>

        <div className="flex items-start gap-3 p-4 bg-secondary/10 border border-secondary/30 rounded-lg">
          <Checkbox
            id="fee-paid"
            checked={formData.feePaid}
            onCheckedChange={(checked) => setFormData({ ...formData, feePaid: checked as boolean })}
          />
          <div className="flex flex-col gap-1">
            <Label 
              htmlFor="fee-paid" 
              className="text-sm font-medium cursor-pointer leading-none"
            >
              Registration fee paid (${LICENSE_FEE.toLocaleString()})
            </Label>
            <p className="text-xs text-muted-foreground">
              Check this box if the fighter has paid their registration fee
            </p>
          </div>
        </div>

        <Button type="submit" size="lg" className="bg-primary hover:bg-primary/90 mt-2">
          <Plus className="w-5 h-5 mr-2" />
          Register Boxer
        </Button>
      </form>
    </Card>
  );
}
